import React from 'react';
import { motion } from 'framer-motion';
import { FaTools, FaMicrochip, FaShieldAlt, FaUsersCog, FaLeaf } from 'react-icons/fa';

const container = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 120, damping: 20 },
  },
};

const features = [
  {
    icon: <FaTools size={30} className="text-yellow-300 mb-4" />,
    title: 'Integrated Services',
    description:
      'One-stop solution for construction, logistics, and infrastructure projects, simplifying vendor management.',
  },
  {
    icon: <FaMicrochip size={30} className="text-yellow-300 mb-4" />,
    title: 'Tech-Driven Operations',
    description:
      'We leverage IoT tracking, BIM design tools, and digital fleet management to ensure efficiency and transparency.',
  },
  {
    icon: <FaShieldAlt size={30} className="text-yellow-300 mb-4" />,
    title: 'Regulatory Compliance',
    description:
      'Fully aligned with local and international safety, environmental, and quality standards.',
  },
  {
    icon: <FaUsersCog size={30} className="text-yellow-300 mb-4" />,
    title: 'Skilled Workforce',
    description:
      'Our multidisciplinary team brings deep experience and innovation to every project we execute.',
  },
  {
    icon: <FaLeaf size={30} className="text-yellow-300 mb-4" />,
    title: 'Sustainable Approach',
    description:
      'Green building practices and eco-friendly logistics that protect the planet and communities.',
  },
];

const WhyChooseUs = () => {
  return (
    <section
      id='why choose us'
      className="relative py-24 px-4 bg-gradient-to-br from-[#0f2027] via-[#203a43] to-[#2c5364] text-white overflow-hidden"
    >
      {/* SVG Particle Background */}
      <svg
        className="absolute inset-0 w-full h-full z-0"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD700" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#002366" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="10" cy="20" r="1" fill="url(#grad)">
          <animate attributeName="cy" values="20;80;20" dur="10s" repeatCount="indefinite" />
        </circle>
        <circle cx="90" cy="30" r="1" fill="url(#grad)">
          <animate attributeName="cy" values="30;70;30" dur="8s" repeatCount="indefinite" />
        </circle>
        <circle cx="50" cy="50" r="1.2" fill="url(#grad)">
          <animate attributeName="cy" values="50;90;50" dur="12s" repeatCount="indefinite" />
        </circle>
        <circle cx="25" cy="70" r="1" fill="url(#grad)">
          <animate attributeName="cy" values="70;20;70" dur="9s" repeatCount="indefinite" />
        </circle>
        <circle cx="75" cy="60" r="1.2" fill="url(#grad)">
          <animate attributeName="cy" values="60;10;60" dur="11s" repeatCount="indefinite" />
        </circle>
      </svg>

      <motion.div
        
        className="relative max-w-7xl mx-auto z-10"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={container}
      >
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 text-transparent bg-clip-text">
          Why Choose Us
        </h2>

        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition duration-300 hover:scale-[1.03]"
            >
              {feature.icon}
              <h3 className="text-xl font-semibold text-yellow-300 mb-3">{feature.title}</h3>
              <p className="text-sm text-gray-200">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default WhyChooseUs;
